import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useEffect, useState, useCallback } from "react";
import { MapPin, Mail, Plus, Minus, Upload, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const inputClass = "w-full bg-transparent border-b border-[#d1d5db] focus:border-[#4F6BFF] outline-none py-3 font-body text-[15px] text-[#08122a] placeholder:text-[#94a3b8] transition-colors duration-200";
const labelClass = "block font-body text-[13px] font-medium text-[#475569] mb-1";
const selectClass = "w-full bg-transparent border-b border-[#d1d5db] focus:border-[#4F6BFF] outline-none py-3 font-body text-[15px] text-[#08122a] transition-colors duration-200 appearance-none cursor-pointer";

function useFormSubmit(formType) {
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const submit = useCallback(async (fields) => {
    setStatus("loading");
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_type: formType, fields }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus("success");
        setMessage(data.message);
      } else {
        setStatus("error");
        setMessage(data.message || "Something went wrong.");
      }
    } catch {
      setStatus("error");
      setMessage("Network error. Please try again.");
    }
  }, [formType]);

  const reset = useCallback(() => { setStatus("idle"); setMessage(""); }, []);

  return { status, message, submit, reset };
}

function SubmitButton({ status, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={status === "loading" || status === "success"}
      className="inline-flex items-center gap-2 px-6 py-3 text-[14px] font-semibold text-white rounded-full transition-all duration-200 group hover:-translate-y-0.5 mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
      style={{ background: status === "success" ? "#16a34a" : "linear-gradient(135deg, #2563EB 0%, #4338CA 50%, #3730A3 100%)" }}
    >
      {status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
      {status === "success" && <CheckCircle2 className="w-4 h-4" />}
      {status === "loading" ? "Sending..." : status === "success" ? "Sent" : "Submit"}
      {status === "idle" && <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
    </button>
  );
}

function StatusMessage({ status, message }) {
  if (!message) return null;
  return (
    <p className={`font-body text-[13px] mt-2 ${status === "success" ? "text-green-600" : "text-red-500"}`}>
      {message}
    </p>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className={labelClass}>
        {label}{required && <span className="text-[#4F6BFF] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function GeneralInquiryForm() {
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", organization: "", website: "", location: "", message: "" });
  const { status, message, submit } = useFormSubmit("General Inquiry");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const handleSubmit = () => {
    if (!f.firstName || !f.email || !f.message) return;
    submit({ "First Name": f.firstName, "Last Name": f.lastName, "Email": f.email, "Organization": f.organization, "Website": f.website, "Location": f.location, "Message": f.message });
  };
  return (
    <div className="space-y-5" data-testid="form-general">
      <FormField label="Name" required>
        <div className="grid grid-cols-2 gap-4">
          <input value={f.firstName} onChange={set("firstName")} placeholder="First Name" className={inputClass} data-testid="field-first-name" />
          <input value={f.lastName} onChange={set("lastName")} placeholder="Last Name" className={inputClass} data-testid="field-last-name" />
        </div>
      </FormField>
      <FormField label="Email" required><input type="email" value={f.email} onChange={set("email")} placeholder="your@email.com" className={inputClass} /></FormField>
      <FormField label="Organization" required><input value={f.organization} onChange={set("organization")} placeholder="Company name" className={inputClass} /></FormField>
      <FormField label="Website"><input type="url" value={f.website} onChange={set("website")} placeholder="https://" className={inputClass} /></FormField>
      <FormField label="Location"><input value={f.location} onChange={set("location")} placeholder="City, Country" className={inputClass} /></FormField>
      <FormField label="Message" required><textarea rows={4} value={f.message} onChange={set("message")} placeholder="Brief overview of company, traction, and capital sought." className={`${inputClass} resize-none`} /></FormField>
      <SubmitButton status={status} onClick={handleSubmit} />
      <StatusMessage status={status} message={message} />
    </div>
  );
}

function InvestmentForm() {
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", organization: "", website: "", location: "", stage: "", sector: "", message: "" });
  const { status, message, submit } = useFormSubmit("Investment Opportunity");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const handleSubmit = () => {
    if (!f.firstName || !f.email || !f.organization || !f.message) return;
    submit({ "First Name": f.firstName, "Last Name": f.lastName, "Email": f.email, "Organization": f.organization, "Website": f.website, "Location": f.location, "Stage": f.stage, "Sector": f.sector, "Message": f.message });
  };
  return (
    <div className="space-y-5" data-testid="form-investment">
      <FormField label="Name" required>
        <div className="grid grid-cols-2 gap-4">
          <input value={f.firstName} onChange={set("firstName")} placeholder="First Name" className={inputClass} />
          <input value={f.lastName} onChange={set("lastName")} placeholder="Last Name" className={inputClass} />
        </div>
      </FormField>
      <FormField label="Email" required><input type="email" value={f.email} onChange={set("email")} placeholder="your@email.com" className={inputClass} /></FormField>
      <FormField label="Organization" required><input value={f.organization} onChange={set("organization")} placeholder="Company name" className={inputClass} /></FormField>
      <FormField label="Website" required><input type="url" value={f.website} onChange={set("website")} placeholder="https://" className={inputClass} /></FormField>
      <FormField label="Location" required><input value={f.location} onChange={set("location")} placeholder="City, Country" className={inputClass} /></FormField>
      <FormField label="Stage" required>
        <select value={f.stage} onChange={set("stage")} className={selectClass}>
          <option value="">Select stage</option>
          <option>Pre-seed</option><option>Seed</option><option>Series A</option>
        </select>
      </FormField>
      <FormField label="Sector" required>
        <select value={f.sector} onChange={set("sector")} className={selectClass}>
          <option value="">Select sector</option>
          <option>Fintech and payments</option><option>Financial infrastructure</option><option>B2B software</option><option>Security and compliance</option><option>Operational systems</option><option>Infrastructure</option><option>Other (please specify)</option>
        </select>
      </FormField>
      <FormField label="Message" required><textarea rows={4} value={f.message} onChange={set("message")} placeholder="Brief overview of company, traction, and capital sought." className={`${inputClass} resize-none`} /></FormField>
      <SubmitButton status={status} onClick={handleSubmit} />
      <StatusMessage status={status} message={message} />
    </div>
  );
}

function CoInvestmentForm() {
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", organization: "", partnerType: "", geoFocus: "", stageFocus: "", message: "" });
  const { status, message, submit } = useFormSubmit("Co-Investment Opportunity");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const handleSubmit = () => {
    if (!f.firstName || !f.email || !f.organization || !f.message) return;
    submit({ "First Name": f.firstName, "Last Name": f.lastName, "Email": f.email, "Organization": f.organization, "Type of Partner": f.partnerType, "Geographic Focus": f.geoFocus, "Investment Stage Focus": f.stageFocus, "Message": f.message });
  };
  return (
    <div className="space-y-5" data-testid="form-coinvestment">
      <FormField label="Name" required>
        <div className="grid grid-cols-2 gap-4">
          <input value={f.firstName} onChange={set("firstName")} placeholder="First Name" className={inputClass} />
          <input value={f.lastName} onChange={set("lastName")} placeholder="Last Name" className={inputClass} />
        </div>
      </FormField>
      <FormField label="Email" required><input type="email" value={f.email} onChange={set("email")} placeholder="your@email.com" className={inputClass} /></FormField>
      <FormField label="Organization" required><input value={f.organization} onChange={set("organization")} placeholder="Company name" className={inputClass} /></FormField>
      <FormField label="Type of partner" required>
        <select value={f.partnerType} onChange={set("partnerType")} className={selectClass}>
          <option value="">Select type</option>
          <option>Venture fund</option><option>Institutional investor</option><option>Angel/syndicate</option><option>Family office</option><option>Other</option>
        </select>
      </FormField>
      <FormField label="Geographic focus"><input value={f.geoFocus} onChange={set("geoFocus")} placeholder="Region or country" className={inputClass} /></FormField>
      <FormField label="Investment stage focus"><input value={f.stageFocus} onChange={set("stageFocus")} placeholder="e.g. Seed, Series A" className={inputClass} /></FormField>
      <FormField label="Message" required><textarea rows={4} value={f.message} onChange={set("message")} placeholder="Brief summary of your request." className={`${inputClass} resize-none`} /></FormField>
      <SubmitButton status={status} onClick={handleSubmit} />
      <StatusMessage status={status} message={message} />
    </div>
  );
}

function MediaInquiryForm() {
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", organization: "", website: "", position: "", topic: "", message: "" });
  const { status, message, submit } = useFormSubmit("Media Inquiry");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const handleSubmit = () => {
    if (!f.firstName || !f.email || !f.organization || !f.message) return;
    submit({ "First Name": f.firstName, "Last Name": f.lastName, "Email": f.email, "Organization": f.organization, "Website": f.website, "Position": f.position, "Topic of Inquiry": f.topic, "Message": f.message });
  };
  return (
    <div className="space-y-5" data-testid="form-media">
      <FormField label="Name" required>
        <div className="grid grid-cols-2 gap-4">
          <input value={f.firstName} onChange={set("firstName")} placeholder="First Name" className={inputClass} />
          <input value={f.lastName} onChange={set("lastName")} placeholder="Last Name" className={inputClass} />
        </div>
      </FormField>
      <FormField label="Email" required><input type="email" value={f.email} onChange={set("email")} placeholder="your@email.com" className={inputClass} /></FormField>
      <FormField label="Organization" required><input value={f.organization} onChange={set("organization")} placeholder="Company or publication" className={inputClass} /></FormField>
      <FormField label="Website" required><input type="url" value={f.website} onChange={set("website")} placeholder="https://" className={inputClass} /></FormField>
      <FormField label="Position"><input value={f.position} onChange={set("position")} placeholder="Your role" className={inputClass} /></FormField>
      <FormField label="Topic of inquiry" required><input value={f.topic} onChange={set("topic")} placeholder="Subject" className={inputClass} /></FormField>
      <FormField label="Message" required><textarea rows={4} value={f.message} onChange={set("message")} placeholder="Brief summary of your request." className={`${inputClass} resize-none`} /></FormField>
      <SubmitButton status={status} onClick={handleSubmit} />
      <StatusMessage status={status} message={message} />
    </div>
  );
}

const formSections = [
  { id: "general", title: "General Inquiry", Form: GeneralInquiryForm },
  { id: "investment", title: "Investment Opportunity", Form: InvestmentForm },
  { id: "coinvestment", title: "Co-investment Opportunity", Form: CoInvestmentForm },
  { id: "media", title: "Media Inquiry", Form: MediaInquiryForm },
];

export default function ContactUs() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [openSection, setOpenSection] = useState(null);

  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, margin: "-40px" });

  const toggle = (id) => setOpenSection((prev) => (prev === id ? null : id));

  return (
    <div className="min-h-screen" style={{ background: "#f8faff" }} data-testid="contact-page">
      <Navbar />

      {/* ═══════ HERO ═══════ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-32 md:pt-40 pb-[9px] md:pb-8"
        style={{ background: "#f8faff" }}
        data-testid="contact-hero"
      >
        <div className="absolute inset-0 grid-pattern opacity-[0.06]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="flex items-center justify-center gap-3 mb-8"
          >
            <div className="w-8 h-px bg-[#4F6BFF]" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#4F6BFF]/80">Contact</span>
            <div className="w-8 h-px bg-[#4F6BFF]" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-6"
            data-testid="contact-heading"
          >
            Get in touch
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="font-body text-[15px] md:text-base leading-[1.75] text-[#475569] max-w-[620px] mx-auto"
          >
            We work with founders and partners across Canada and beyond.{' '}
            <br className="hidden md:inline" />
            Reach us anytime at{" "}
            <br className="md:hidden" />
            <a href="mailto:info@nebula-f.com" className="text-[#4F6BFF] hover:underline">info@nebula-f.com</a>{" "}
            or use the form below.
          </motion.p>
        </div>
      </section>

      {/* ═══════ TWO-COLUMN: CONTACT INFO + FORMS ═══════ */}
      <section className="pt-[30px] pb-[60px] md:py-[50px]" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            {/* Left — contact info cards stacked */}
            <div className="lg:col-span-4 lg:sticky lg:top-28 space-y-4">
              {[
                { icon: MapPin, label: "Address", value: "2 Eastbourne Terrace, Paddington,\nLondon W2 6LG, UK" },
                { icon: Mail, label: "Email", value: "info@nebula-f.com", href: "mailto:info@nebula-f.com" },
              ].map((item, i) => (
                <Reveal key={item.label} delay={i * 0.07}>
                  <div
                    className="rounded-2xl border border-black/[0.04] p-6 bg-white card-gradient-line portfolio-card"
                    data-testid={`contact-card-${item.label.toLowerCase()}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4F6BFF]/10 to-[#7B61FF]/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-[18px] h-[18px] text-[#4F6BFF]" />
                      </div>
                      <div>
                        <span className="font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-[#94a3b8] block mb-1">
                          {item.label}
                        </span>
                        {item.href ? (
                          <a href={item.href} className="font-body text-[16px] font-medium text-[#08122a] hover:text-[#4F6BFF] transition-colors leading-snug">
                            {item.value}
                          </a>
                        ) : item.label === "Address" ? (
                          <p className="font-body text-[16px] font-medium text-[#08122a] leading-snug">
                            <span className="md:hidden">
                              2 Eastbourne Terrace<br />
                              <span className="whitespace-nowrap">Paddington, London W2 6LG, UK</span>
                            </span>
                            <span className="hidden md:inline whitespace-pre-line">
                              {item.value}
                            </span>
                          </p>
                        ) : (
                          <p className="font-body text-[16px] font-medium text-[#08122a] leading-snug whitespace-pre-line">
                            {item.value}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Right — expandable form accordions */}
            <div className="lg:col-span-8" data-testid="contact-forms-section">
              <div className="space-y-3">
                {formSections.map((section, i) => {
                  const isOpen = openSection === section.id;
                  return (
                    <Reveal key={section.id} delay={i * 0.05}>
                      <div
                        className="rounded-2xl border border-black/[0.04] overflow-hidden transition-shadow duration-300"
                        style={isOpen ? { boxShadow: "0 8px 32px rgba(15,23,42,0.06), 0 2px 8px rgba(15,23,42,0.03)" } : {}}
                        data-testid={`accordion-${section.id}`}
                      >
                        <button
                          onClick={() => toggle(section.id)}
                          className="w-full flex items-center justify-between px-7 py-5 group cursor-pointer"
                          style={{ background: isOpen ? "#fafbfe" : "#ffffff" }}
                          data-testid={`accordion-toggle-${section.id}`}
                        >
                          <span className="font-body text-[17px] font-semibold text-[#08122a] tracking-tight group-hover:text-[#3B5BDB] transition-colors">
                            {section.title}
                          </span>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center border border-black/[0.06] group-hover:border-[#4F6BFF]/30 transition-colors">
                            {isOpen ? (
                              <Minus className="w-4 h-4 text-[#4F6BFF]" />
                            ) : (
                              <Plus className="w-4 h-4 text-[#475569] group-hover:text-[#4F6BFF] transition-colors" />
                            )}
                          </div>
                        </button>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                              className="overflow-hidden"
                            >
                              <div className="px-7 pb-7 pt-2" style={{ background: "#fafbfe" }}>
                                <div className="h-px bg-black/[0.04] mb-6" />
                                <section.Form />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
